import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonIcon, IonInput, IonItem, IonLabel, IonList, IonListHeader, IonModal, IonSelect, IonSelectOption, IonText } from "@ionic/react";
import { Assignment, ClassSidebarVisibility, Quiz, StudentEnrollmentFromServer, StudentStatusFromServer } from "../../../data-types/Types";
import { useData } from "../services/DataProvider";
import CreateAssignment from "./CreateAssignment";
import Expandable from "./Expandable";
import Standard, { formatStandardCode } from "./Standard";
import TableActions from './TableActions';
import ClassInsights from "./ClassInsights";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { closeOutline, trashOutline } from "ionicons/icons";
import "./ClassManagement.css";

// Define explicit types for the summary object
type AssignmentSummary = {
  completed: number;
  quizName: string | undefined;
  inferredKnowledgeByStandard: {[studentID: string]: {[standardID: string]: number}};
};

const ClassManagement: React.FC<{classID: string}> = ({classID, ...props}) => {
  const link = (window.location.protocol + "//") + window.location.hostname.toLowerCase() + (window.location.port ? ":" + window.location.port : "") + "/class/" + classID;
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

  const {standards, teacherData, deleteClass, renameClass, updateClassSidebarVisibility, synching} = useData();
  const history = useHistory();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [renameText, setRenameText] = useState("");
  const [renameError, setRenameError] = useState("");
  const [visibilityError, setVisibilityError] = useState("");
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const enrolledStudents = (teacherData.classesToEnrollments?.[classID] || []) as StudentEnrollmentFromServer[];
  const assignments = (teacherData.classesToAssignments?.[classID] || []) as Assignment[];

  // Create assignment summaries using the data from teacherData.assignmentsToMasteryData
  const assignmentsToSummaries: Record<string, AssignmentSummary> = Object.fromEntries(assignments.map((assignment) => {
    const statuses = (teacherData.assignmentsToStatuses?.[assignment.id || ""] || []) as StudentStatusFromServer[];
    const quizName = teacherData.quizzes?.filter((quiz: Quiz) => quiz.id?.trim() === assignment.quizID.trim())?.[0]?.name;

    // Get mastery data from the server
    const masteryData = teacherData.assignmentsToMasteryData?.[assignment.id || ""];
    
    return [assignment.id, {
      completed: statuses.length,
      quizName: quizName,
      inferredKnowledgeByStandard: masteryData?.inferredKnowledgeByStandard || {}
    }];
  }));

  const classRecord = teacherData.classes?.find(c => c.shortCode === classID);
  const classData = classRecord || {displayName: "", shortCode: classID};
  const canDeleteClass = classData.shortCode !== "demo";
  const canConfirmDelete = confirmText.trim() === classData.shortCode && !!classRecord?.id;
  const trimmedRename = renameText.trim();
  const canSaveRename = !!classRecord?.id && trimmedRename.length > 0 && trimmedRename !== classData.displayName;
  const sidebarVisibility: ClassSidebarVisibility = classRecord?.sidebarVisibility || "automatic";

  return (
    <>
      <ClassInsights
        classData={classData}
        assignments={assignments}
        enrollments={enrolledStudents}
        assignmentsToSummaries={assignmentsToSummaries}
        standards={standards}
        isReadOnly={false}
      />
      {canDeleteClass && (
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Class Settings</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <div style={{ display: "flex", gap: "0.5em", flexWrap: "wrap" }}>
              <IonButton
                size="small"
                onClick={() => {
                  setRenameError("");
                  setRenameText(classData.displayName || "");
                  setShowRenameModal(true);
                }}
                disabled={!classRecord?.id}
              >
                Rename class
              </IonButton>
              <IonButton
                size="small"
                color="danger"
                onClick={() => {
                  setDeleteError("");
                  setConfirmText("");
                  setShowDeleteModal(true);
                }}
              >
                <IonIcon icon={trashOutline} slot="start" />
                Delete class
              </IonButton>
            </div>
            <IonItem className="ion-margin-top">
              <IonLabel position="stacked">Class visibility</IonLabel>
              <IonSelect
                aria-label="Class visibility"
                value={sidebarVisibility}
                disabled={!classRecord?.id || synching}
                onIonChange={async (event) => {
                  const nextVisibility = event.detail.value as ClassSidebarVisibility;
                  if (!classRecord?.id || nextVisibility === sidebarVisibility) {
                    return;
                  }
                  setVisibilityError("");
                  try {
                    await updateClassSidebarVisibility(classRecord.id, nextVisibility);
                  } catch (error: any) {
                    if (isMountedRef.current) {
                      setVisibilityError(error?.message || "Failed to update class visibility.");
                    }
                  }
                }}
              >
                <IonSelectOption value="automatic">Automatic (recommended)</IonSelectOption>
                <IonSelectOption value="always-visible">Keep in sidebar</IonSelectOption>
                <IonSelectOption value="manually-archived">Move to inactive classes</IonSelectOption>
              </IonSelect>
            </IonItem>
            <IonText color="medium">
              <p>
                Automatic classes move to Inactive classes 10 calendar months after their most recent
                class or assignment date. This only changes the teacher sidebar.
              </p>
            </IonText>
            {visibilityError && <IonText color="danger"><p>{visibilityError}</p></IonText>}
            <IonModal
              isOpen={showRenameModal}
              onDidDismiss={() => setShowRenameModal(false)}
              className="class-delete-modal"
            >
              <IonCard>
                <IonCardHeader style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <IonCardTitle>Rename class</IonCardTitle>
                  <IonButton
                    fill="clear"
                    color="medium"
                    aria-label="Close"
                    onClick={() => setShowRenameModal(false)}
                  >
                    <IonIcon icon={closeOutline} />
                  </IonButton>
                </IonCardHeader>
                <IonCardContent>
                  <p>Enter a new name for this class.</p>
                  <IonInput
                    value={renameText}
                    onIonChange={(event) => setRenameText(event.detail.value || "")}
                    placeholder="Class name"
                  />
                  {renameError && <IonText color="danger"><p>{renameError}</p></IonText>}
                  <div style={{ display: "flex", gap: "0.5em", marginTop: "1em" }}>
                    <IonButton onClick={() => setShowRenameModal(false)} disabled={synching}>
                      Cancel
                    </IonButton>
                    <IonButton
                      disabled={!canSaveRename || synching}
                      onClick={async () => {
                        if (!classRecord?.id) {
                          if (isMountedRef.current) {
                            setRenameError("Missing class id.");
                          }
                          return;
                        }
                        try {
                          if (isMountedRef.current) {
                            setShowRenameModal(false);
                          }
                          await renameClass(classRecord, trimmedRename);
                        } catch (error: any) {
                          if (isMountedRef.current) {
                            setRenameError(error?.message || "Failed to rename class.");
                          }
                        }
                      }}
                    >
                      Save
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>
            </IonModal>
            <IonModal
              isOpen={showDeleteModal}
              onDidDismiss={() => setShowDeleteModal(false)}
              className="class-delete-modal"
            >
              <IonCard>
                <IonCardHeader style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <IonCardTitle>Delete class</IonCardTitle>
                  <IonButton
                    fill="clear"
                    color="medium"
                    aria-label="Close"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    <IonIcon icon={closeOutline} />
                  </IonButton>
                </IonCardHeader>
                <IonCardContent>
                  <p>This will hide the class and remove it from all views.</p>
                  <p>Type the class code <b>{classData.shortCode}</b> to confirm.</p>
                  <IonInput
                    value={confirmText}
                    onIonChange={(event) => setConfirmText(event.detail.value || "")}
                    placeholder="Class code"
                  />
                  {deleteError && <IonText color="danger"><p>{deleteError}</p></IonText>}
                  <div style={{ display: "flex", gap: "0.5em", marginTop: "1em" }}>
                    <IonButton onClick={() => setShowDeleteModal(false)} disabled={synching}>
                      Cancel
                    </IonButton>
                    <IonButton
                      color="danger"
                      disabled={!canConfirmDelete || synching}
                      onClick={async () => {
                        if (!classRecord?.id) {
                          if (isMountedRef.current) {
                            setDeleteError("Missing class id.");
                          }
                          return;
                        }
                        try {
                          if (isMountedRef.current) {
                            setShowDeleteModal(false);
                          }
                          await deleteClass(classRecord.id);
                          history.push("/");
                        } catch (error: any) {
                          if (isMountedRef.current) {
                            setDeleteError(error?.message || "Failed to delete class.");
                          }
                        }
                      }}
                    >
                      Delete
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>
            </IonModal>
          </IonCardContent>
        </IonCard>
      )}
    </>
  );
}

export default ClassManagement;
